import { create } from 'zustand';
import { api } from './api';
import { tokens } from './tokens';
import { clearAffiliateRef, getAffiliateRef } from './affiliateRef';
import type { AuthTokens, Me, PayoutMethod } from './types';

/** Discriminated result of a register call — either signed in immediately, or pending verification. */
export type RegisterResult =
  | { kind: 'signed-in' }
  | { kind: 'pending'; email: string };

type Status = 'loading' | 'authed' | 'guest';

interface AuthState {
  user: Me | null;
  status: Status;
  init: () => Promise<void>;
  login: (email: string, password: string, turnstileToken?: string | null) => Promise<void>;
  register: (
    email: string,
    password: string,
    tenantName: string,
    /** Auto-promote the new tenant to agency status on first login. */
    asAgency?: boolean,
    /** Cloudflare Turnstile token, when the abuse-protection layer is enabled. */
    turnstileToken?: string | null,
  ) => Promise<RegisterResult>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  applyAffiliate: (args: {
    email: string;
    password: string;
    displayName?: string;
    payoutMethod?: PayoutMethod;
    payoutData?: Record<string, string>;
  }) => Promise<RegisterResult>;
  verifyEmail: (rawToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

async function loadTokensAndUser(tk: AuthTokens, set: (s: Partial<AuthState>) => void) {
  tokens.set(tk.accessToken, tk.refreshToken);
  const user = await api<Me>('/auth/me');
  set({ user, status: 'authed' });
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',

  async init() {
    if (!tokens.access && !tokens.refresh) {
      set({ status: 'guest' });
      return;
    }
    try {
      const user = await api<Me>('/auth/me');
      set({ user, status: 'authed' });
    } catch {
      tokens.clear();
      set({ user: null, status: 'guest' });
    }
  },

  async login(email, password, turnstileToken) {
    const tk = await api<AuthTokens>('/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
      headers: { 'X-Turnstile-Token': turnstileToken ?? undefined },
    });
    await loadTokensAndUser(tk, set);
  },

  async register(email, password, tenantName, asAgency, turnstileToken) {
    const result = await api<AuthTokens | { pending: true; email: string }>('/auth/register', {
      method: 'POST',
      body: { email, password, tenantName, ref: getAffiliateRef() },
      auth: false,
      headers: { 'X-Turnstile-Token': turnstileToken ?? undefined },
    });
    clearAffiliateRef(); // consumed — don't attribute future signups to the same code
    // Discriminate: server returns { pending: true, email } when the operator
    // has requireEmailVerification on. Caller (RegisterPage) branches on kind.
    if ('pending' in result && result.pending) {
      return { kind: 'pending', email: result.email };
    }
    await loadTokensAndUser(result as AuthTokens, set);
    if (asAgency) {
      // Two-step on purpose: register's payload contract stays untouched (every other
      // existing surface keeps working), and an agency signup just chains the same
      // /agency/enable endpoint a standalone tenant would use to upgrade later.
      await api('/agency/enable', { method: 'POST' });
      // Refresh the cached `me` so `tenant.isAgency` is true on the next render —
      // unlocks the workspace switcher + the /app/agency console immediately.
      const user = await api<Me>('/auth/me');
      set({ user });
    }
    return { kind: 'signed-in' };
  },

  async applyAffiliate({ email, password, displayName, payoutMethod, payoutData }) {
    const result = await api<AuthTokens | { pending: true; email: string }>('/affiliate/apply', {
      method: 'POST',
      body: { email, password, displayName, payoutMethod, payoutData },
      auth: false,
    });
    if ('pending' in result && result.pending) {
      return { kind: 'pending', email: result.email };
    }
    await loadTokensAndUser(result as AuthTokens, set);
    return { kind: 'signed-in' };
  },

  async verifyEmail(rawToken) {
    const tk = await api<AuthTokens>('/auth/verify-email', {
      method: 'POST',
      body: { token: rawToken },
      auth: false,
    });
    await loadTokensAndUser(tk, set);
  },

  async acceptInvite(token, password) {
    const tk = await api<AuthTokens>('/auth/accept-invite', {
      method: 'POST',
      body: { token, password },
      auth: false,
    });
    await loadTokensAndUser(tk, set);
  },

  async logout() {
    try {
      await api('/auth/logout', { method: 'POST', body: { refreshToken: tokens.refresh } });
    } catch {
      // ignore — clearing locally regardless
    }
    tokens.clear();
    set({ user: null, status: 'guest' });
  },

  async refreshUser() {
    const user = await api<Me>('/auth/me');
    set({ user });
  },
}));
